/**
 * Generic Image Assets Section for any entity type.
 * Supports Performance Max asset groups, campaigns, and ad groups.
 * Shows images uploaded via Flouri with thumbnails from Media Library.
 * Allows uploading new images and managing existing ones (remove, change type).
 *
 * Key principle: Only show images uploaded via Flouri (which have reliable GCS thumbnails).
 * Images uploaded directly via Google Ads UI are not shown to avoid thumbnail issues.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { PencilIcon, TrashIcon } from '@/components/ui/Icons';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { batchDeleteImages, createTargetedImage } from '@/services/api/media';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import {
  saveImageAssetsGeneric,
  getLinkedImagesByEntity,
  removeImageFromEntity,
  changeImageAssetType,
  type ImageAssetType,
  type ImageEntityType,
  type LinkedImageInfo,
  type GoogleAdsErrorDetails,
  type SaveImageAssetsResponse,
} from '@/services/api/assets';
import { debug, toast } from '@/utils';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { GoogleRestrictionDialog } from '@/components/ui/GoogleRestrictionDialog';
import { usePlatformSpecs } from '@/hooks/queries/usePlatformSpecs';

/** Image type option for the picker */
interface ImageTypeOption {
  value: ImageAssetType;
  label: string;
  description: string;
  aspectRatio: string;
}

interface ImageAssetsSectionProps {
  memberUuid: string;
  entityType: ImageEntityType;
  entityId: string;
  entityName?: string;
  customerId?: string;
  campaignType?: string;  // Determines valid image types (e.g., 'PERFORMANCE_MAX', 'SEARCH', 'DISPLAY')
  campaignId?: string;    // Required for ad_group entity type
  imageTypeOptions?: ImageTypeOption[];  // Override default options if needed
  /**
   * Ad platform this section uploads to. Currently restricted to 'google_ads'
   * because the upload payload (`saveImageAssetsGeneric`) and the local
   * `ImageAssetType` union are Google-Ads-specific (`marketing_image`,
   * `square_marketing_image`, etc.). Widening to `Platform` (i.e. allowing
   * 'meta_ads') happens with Layer B when a Meta upload pipeline lands —
   * see docs/CREATIVE_STUDIO_PLATFORM_GENERALIZATION.md.
   */
  platform?: 'google_ads';
  onSuccess?: () => void;
}

// Hardcoded ALL_IMAGE_TYPE_OPTIONS / NON_PMAX_IMAGE_TYPE_OPTIONS were removed
// in favor of `usePlatformSpecs(platform, ad_type)` driving the option list
// from the backend spec registry — see `src/api/models/platform_specs.py`.
//
// The PMax-vs-non-PMax branch that used to live here is replaced by each
// spec carrying its own `eligible_ad_types`. Adding a new placement (Google
// or Meta) is a backend-only change.

export function ImageAssetsSection({
  memberUuid,
  entityType,
  entityId,
  entityName,
  customerId,
  campaignType,
  campaignId,
  imageTypeOptions: customImageTypeOptions,
  platform = 'google_ads',
  onSuccess,
}: ImageAssetsSectionProps) {
  // Spec-driven image type options. We pass the campaignType through as the
  // `ad_type` filter so the backend's `eligible_ad_types` rules decide which
  // placements show up — replacing the old hardcoded PMax vs non-PMax branch.
  // Search/Display campaigns historically map to "Image Extensions" which is
  // expressed in the registry by including the Search/Performance Max-style
  // ad_type tokens on the relevant specs.
  const adTypeForFilter = useMemo(() => {
    if (!campaignType) return undefined;
    if (campaignType.includes('PERFORMANCE_MAX')) return 'PERFORMANCE_MAX';
    if (campaignType.includes('DEMAND_GEN')) return 'DEMAND_GEN_MULTI_ASSET_AD';
    if (campaignType.includes('DISPLAY')) return 'RESPONSIVE_DISPLAY_AD';
    return 'SEARCH';
  }, [campaignType]);

  const { data: platformSpecs } = usePlatformSpecs({
    platform,
    ad_type: adTypeForFilter,
  });

  // Until specs resolve, `imageTypeOptions` is empty. We deliberately do NOT
  // fall back to a hardcoded Google option list — the component now accepts
  // a `platform` prop, and showing Google placements briefly when platform
  // is e.g. 'meta_ads' would be wrong. The dropdown shows a "Loading..."
  // placeholder during this brief window (see render below); the linked-images
  // grouping iterates the linked types directly with a humanized fallback
  // label, so existing attachments stay visible even before specs load.
  const imageTypeOptions: ImageTypeOption[] = useMemo(() => {
    if (customImageTypeOptions && customImageTypeOptions.length > 0) {
      return customImageTypeOptions;
    }
    if (!platformSpecs) return [];
    return platformSpecs.map((spec) => ({
      // For now `value` is the bare placement_id so it round-trips through the
      // legacy `image_field_type` upload payload. When the upload pipeline moves
      // to Targeted images (task 11), this becomes `spec.key` end-to-end.
      value: spec.placement_id as ImageAssetType,
      label: spec.display_name,
      description: `${spec.display_name} (${spec.aspect_ratio_label})`,
      aspectRatio: spec.aspect_ratio_label,
    }));
  }, [customImageTypeOptions, platformSpecs]);

  // State for image picker and upload.
  //
  // We track **PendingTargeted** records (in-memory only) — each holds the
  // source MediaImage, the placement key, and the user's chosen crop box.
  // Preview thumbnails are rendered via `CroppedThumbnail` (CSS background
  // positioning over `source.signed_url`), not Canvas — there's no data URL
  // in state. Targeted MediaImages are NOT persisted to GCS until the user
  // actually clicks "Upload to Google Ads" — at that point we materialize
  // them via parallel POSTs to /media/images/:id/targeted, then submit the
  // resulting Targeted IDs to /assets/images/save-generic. This avoids GCS
  // pollution: re-cropping, abandoning the picker, or never clicking Upload
  // all leave GCS untouched.
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedPending, setSelectedPending] = useState<PendingTargeted[]>([]);
  const [selectedImageType, setSelectedImageType] = useState<ImageAssetType>(
    imageTypeOptions[0]?.value || 'marketing_image'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Auto-clear success messages after 5 seconds
  useEffect(() => {
    if (message?.type === 'success') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // State for remove/change type dialogs
  const [imageToRemove, setImageToRemove] = useState<LinkedImageInfo | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [imageToChangeType, setImageToChangeType] = useState<LinkedImageInfo | null>(null);
  const [newImageType, setNewImageType] = useState<ImageAssetType | null>(null);
  const [isChangingType, setIsChangingType] = useState(false);

  // State for Google restriction dialog
  const [restrictionDetails, setRestrictionDetails] = useState<GoogleAdsErrorDetails | null>(null);

  // Get entity type label for display
  const entityTypeLabel = useMemo(() => {
    switch (entityType) {
      case 'asset_group': return 'Asset Group';
      case 'campaign': return 'Campaign';
      case 'ad_group': return 'Ad Group';
      default: return 'Entity';
    }
  }, [entityType]);

  // Fetch Flouri-linked images with React Query caching
  // staleTime: 5 minutes - data is considered fresh for 5 minutes (no refetch on tab switch)
  // cacheTime: 30 minutes - cached data kept for 30 minutes even when unmounted
  const {
    data: linkedImagesResponse,
    isLoading: isLoadingLinked,
    error: linkedQueryError,
    refetch: refetchLinkedImages,
  } = useQuery({
    queryKey: ['linkedImages', entityType, entityId, memberUuid],
    queryFn: async () => {
      const response = await getLinkedImagesByEntity({
        member_uuid: memberUuid,
        entity_type: entityType,
        entity_id: entityId,
        customer_id: customerId,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to load images');
      }
      debug.log('Fetched linked images', { entityType, entityId, count: response.count });
      return response;
    },
    enabled: !!entityId && !!memberUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevents refetch when switching tabs
    gcTime: 30 * 60 * 1000, // 30 minutes cache retention
  });

  const linkedImages = linkedImagesResponse?.images || [];
  const linkedError = linkedQueryError ? (linkedQueryError as Error).message : null;

  // Force refetch of linked images (bypasses staleTime)
  const fetchLinkedImages = useCallback(async () => {
    debug.log('Forcing refetch of linked images');
    await refetchLinkedImages();
  }, [refetchLinkedImages]);

  // Selected items for preview. Each `PendingTargeted` carries the source
  // MediaImage plus the chosen placementKey + cropBox; previews are rendered
  // via `CroppedThumbnail` (CSS background positioning over `source.signed_url`),
  // not Canvas. Nothing is persisted to GCS until the user clicks Upload.
  const selectedImages = selectedPending;

  // Group linked images by their actual `image_field_type` (whatever the
  // backend gave us). Building dynamically — instead of pre-allocating fixed
  // Google keys — means new placements added to the spec registry, or any
  // legacy field_type the registry doesn't know about, still render here.
  const linkedImagesByType = useMemo(() => {
    const byType: Record<string, LinkedImageInfo[]> = {};
    linkedImages.forEach((img: LinkedImageInfo) => {
      const type = img.image_field_type || 'marketing_image';
      (byType[type] ||= []).push(img);
    });
    return byType;
  }, [linkedImages]);

  // Handle image selection from picker — receives the in-memory pending
  // crops so the preview thumbnails match what will land in Google Ads
  // without writing anything to GCS until the user actually clicks Upload.
  const handlePendingSelected = (pending: PendingTargeted[]) => {
    setSelectedPending(pending);
    setIsPickerOpen(false);
  };

  const handleRemoveImage = (key: string) => {
    setSelectedPending((prev) => prev.filter((p) => pendingKey(p) !== key));
  };

  // Upload selected images to Google Ads. Materializes Targeted images
  // server-side now (parallel POSTs), then submits the resulting Targeted
  // IDs to /assets/images/save-generic. This is the only point in the
  // flow that writes to GCS — abandoned crops never reach the bucket.
  const handleUploadToGoogleAds = async () => {
    if (selectedPending.length === 0) {
      toast.warning('Please select at least one image');
      return;
    }
    if (!memberUuid) {
      toast.error('Not authenticated.');
      return;
    }

    setIsSaving(true);
    setMessage(null);

    try {
      debug.log('Materializing Targeted derivatives + uploading to Google Ads', {
        entity_type: entityType,
        entity_id: entityId,
        image_count: selectedPending.length,
        image_type: selectedImageType,
      });

      // Materialize each pending crop. Done in parallel — these calls each
      // do a GCS download + crop + GCS upload + DB row, so serializing
      // would be O(n × ~3s) for typical sizes. `Promise.allSettled` (not
      // `Promise.all`) so that if one call fails, the successes are still
      // returned and we can best-effort delete them — preserves the "no
      // orphan Targeted unless the user successfully uploads" invariant.
      const settled = await Promise.allSettled(
        selectedPending.map((p) =>
          createTargetedImage(p.source.id, {
            member_uuid: memberUuid,
            placement_key: p.placementKey,
            crop_box: p.cropBox,
          }),
        ),
      );
      const fulfilled = settled.flatMap((r) =>
        r.status === 'fulfilled' ? [r.value] : [],
      );
      const firstReject = settled.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      if (firstReject) {
        if (fulfilled.length > 0) {
          try {
            await batchDeleteImages({
              member_uuid: memberUuid,
              image_ids: fulfilled.map((t) => t.id),
            });
          } catch (cleanupErr) {
            console.warn('Targeted partial-success cleanup failed', cleanupErr);
          }
        }
        throw firstReject.reason instanceof Error
          ? firstReject.reason
          : new Error(String(firstReject.reason));
      }
      const targetedIds = fulfilled.map((t) => t.id);

      // If `saveImageAssetsGeneric` fails after a successful materialization
      // pass, the Targeted rows are also orphaned (created but never linked).
      // Catch + cleanup before re-throwing keeps the same invariant.
      let response: SaveImageAssetsResponse;
      try {
        response = await saveImageAssetsGeneric({
          member_uuid: memberUuid,
          entity_type: entityType,
          entity_id: entityId,
          customer_id: customerId,
          image_ids: targetedIds,
          image_type: selectedImageType,
          campaign_id: campaignId,
          trigger_data_sync: true,
        });
      } catch (saveErr) {
        try {
          await batchDeleteImages({
            member_uuid: memberUuid,
            image_ids: targetedIds,
          });
        } catch (cleanupErr) {
          console.warn('Targeted post-materialize cleanup failed', cleanupErr);
        }
        throw saveErr;
      }

      debug.log('Upload response', response);

      if (response.success) {
        toast.success(response.message);
        // Clear selection after successful upload
        setSelectedPending([]);
        // Refresh linked images
        fetchLinkedImages();
        onSuccess?.();
      } else {
        // Check for Google account restriction error
        if (response.error_code === 'ACCOUNT_NOT_ELIGIBLE_FOR_IMAGE_ASSETS' && response.error_details) {
          setRestrictionDetails(response.error_details);
          // Don't show the regular error message, the dialog will explain it
        } else {
          setMessage({
            type: 'error',
            text: response.message || 'Failed to upload images',
          });
        }
      }

      // Show details about failed images
      if (response.failed_images.length > 0) {
        console.error('Failed images:', response.failed_images);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      // Axios wraps server errors as `{ response: { data: { detail } } }`.
      // FastAPI's HTTPException puts its message there — that's where the
      // useful explanation lives (e.g. the resize-cap reject text from
      // `targeted_image_service`). Falling back to error.message gives the
      // generic "Request failed with status code 400" that's not actionable.
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const errorMsg = detail
        || (error instanceof Error ? error.message : 'Failed to upload images');
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle removing an image from the entity
  const handleRemoveLinkedImage = async () => {
    if (!imageToRemove || !imageToRemove.google_ads_asset_id) return;

    setIsRemoving(true);
    try {
      const response = await removeImageFromEntity({
        member_uuid: memberUuid,
        entity_type: entityType,
        entity_id: entityId,
        google_ads_asset_id: imageToRemove.google_ads_asset_id,
        customer_id: customerId,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(`Image removed from ${entityTypeLabel.toLowerCase()}`);
        setImageToRemove(null);
        // Refresh linked images - await to ensure UI updates
        await fetchLinkedImages();
        onSuccess?.();
      } else {
        toast.error(response.error || 'Failed to remove image');
      }
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image');
    } finally {
      setIsRemoving(false);
    }
  };

  // Handle changing image type (supports asset_group, ad_group, campaign)
  const handleChangeImageType = async () => {
    if (!imageToChangeType || !imageToChangeType.google_ads_asset_id || !imageToChangeType.image_id || !newImageType) return;

    setIsChangingType(true);
    try {
      const response = await changeImageAssetType({
        member_uuid: memberUuid,
        entity_id: entityId,
        entity_type: entityType as 'asset_group' | 'ad_group' | 'campaign',
        google_ads_asset_id: imageToChangeType.google_ads_asset_id,
        new_image_type: newImageType,
        image_id: imageToChangeType.image_id,  // Pass image_id for re-cropping
        customer_id: customerId,
        campaign_id: campaignId,
        trigger_data_sync: true,
      });

      if (response.success) {
        toast.success(`Image re-cropped and uploaded as ${newImageType.replace(/_/g, ' ')}`);
        setImageToChangeType(null);
        setNewImageType(null);
        // Refresh linked images
        fetchLinkedImages();
        onSuccess?.();
      } else {
        toast.error(response.error || 'Failed to change image type');
      }
    } catch (error) {
      console.error('Error changing image type:', error);
      toast.error('Failed to change image type');
    } finally {
      setIsChangingType(false);
    }
  };

  // Check if change type is supported - allow if there are multiple image type options
  // AND the entity type supports the change (not supported for 'ad' entity type - RDAs)
  // This works for PMax asset groups (5 types) and Search/Display campaigns (2 types)
  const supportsChangeType = imageTypeOptions.length > 1 && entityType !== 'ad';

  return (
    <div className="space-y-6">
      {/* Flouri-Linked Images Section - Images uploaded via Flouri with reliable thumbnails */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Images Linked to {entityTypeLabel} ({linkedImages.length})
          {entityName && <span className="font-normal text-gray-500"> - {entityName}</span>}
        </h4>

        {isLoadingLinked ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-3" label="Loading images" />
            <p className="text-gray-600">Loading images...</p>
          </div>
        ) : linkedError ? (
          <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
            <p className="text-red-600">{linkedError}</p>
            <Button variant="secondary" onClick={fetchLinkedImages} className="mt-2">
              Retry
            </Button>
          </div>
        ) : linkedImages.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No images linked to this {entityTypeLabel.toLowerCase()}</p>
            <p className="text-sm text-gray-500 mt-1">
              Upload images from your Media Library below
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Group Flouri-linked images by type. Iterates the actual types
                present in the linked images (not a hardcoded list, not
                `imageTypeOptions`) so existing attachments stay visible while
                platform specs are loading, the spec fetch fails, OR the
                registry adds new placements not yet in the local hardcoded
                list. Label comes from the spec when available, otherwise a
                humanized fallback derived from the type key. */}
            {Object.entries(linkedImagesByType).map(([typeKey, images]) => {
              if (images.length === 0) return null;
              const option = imageTypeOptions.find((opt) => opt.value === typeKey);
              const label = option?.label
                ?? typeKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

              return (
                <div key={typeKey} className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">
                    {label} ({images.length})
                  </h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {images.map((img) => (
                      <div
                        key={img.usage_id}
                        className="relative bg-white rounded border border-gray-200 overflow-hidden group"
                        title={img.filename}
                      >
                        <div className="aspect-square bg-gray-100">
                          {img.thumbnail_url ? (
                            <img
                              src={img.thumbnail_url}
                              alt={img.filename}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <PhotoIcon className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>
                        {/* Hover overlay with actions */}
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {supportsChangeType && (
                            <button
                              onClick={() => {
                                setImageToChangeType(img);
                                setNewImageType(null);
                              }}
                              className="p-1.5 bg-white text-gray-700 rounded hover:bg-blue-500 hover:text-white transition-colors"
                              title="Change type"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setImageToRemove(img)}
                            className="p-1.5 bg-white text-gray-700 rounded hover:bg-red-500 hover:text-white transition-colors"
                            title={`Remove from ${entityTypeLabel.toLowerCase()}`}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Filename */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                          <p className="text-[10px] text-white truncate">{img.filename}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Add New Images from Media Library
        </h4>

        {/* Status Message - click to dismiss */}
        {message && (
          <div
            className={`p-3 rounded-md text-sm mb-4 cursor-pointer ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : message.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}
            onClick={() => setMessage(null)}
            title="Click to dismiss"
          >
            {message.text}
          </div>
        )}

        {/* Selected Images Preview */}
        {selectedImages.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-xs font-medium text-blue-700">
                Selected for Upload ({selectedImages.length})
              </h5>
              <span className="text-xs text-blue-600">
                Type: {imageTypeOptions.find((opt: ImageTypeOption) => opt.value === selectedImageType)?.label}
              </span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {selectedImages.map((pending) => {
                const key = pendingKey(pending);
                return (
                  <div
                    key={key}
                    className="relative group bg-white rounded border border-blue-200 overflow-hidden"
                  >
                    <div className="aspect-square bg-gray-100">
                      <CroppedThumbnail
                        source={pending.source}
                        cropBox={pending.cropBox}
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveImage(key)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Image-type selector — picker is scoped to one placement at a time
              so the crop modal can target the right aspect ratio. */}
          {imageTypeOptions.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span className="font-medium">Type:</span>
              <select
                value={selectedImageType}
                onChange={(e) => {
                  // Clear any pending crops + close the picker — they were
                  // cropped against the old placement's aspect ratio. Without
                  // this, the user could pick square crops, switch the
                  // dropdown to landscape, and upload the square bytes
                  // labeled as a landscape image_type — the bytes wouldn't
                  // match the spec the backend / Google Ads expect.
                  setSelectedImageType(e.target.value as ImageAssetType);
                  setSelectedPending([]);
                  setIsPickerOpen(false);
                }}
                className="border border-gray-300 rounded-md pl-2 pr-8 py-1.5 text-sm min-w-[200px] focus:ring-primary-500 focus:border-primary-500"
                aria-label="Image type for selection"
              >
                {imageTypeOptions.map((opt: ImageTypeOption) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <Button
            variant="secondary"
            onClick={() => setIsPickerOpen(true)}
            className="flex items-center justify-center gap-2"
          >
            <PhotoIcon className="w-4 h-4" />
            {selectedImages.length > 0 ? 'Change Selection' : 'Select from Media Library'}
          </Button>

          {selectedImages.length > 0 && (
            <Button
              variant="primary"
              onClick={handleUploadToGoogleAds}
              disabled={isSaving}
              className="flex items-center justify-center gap-2"
            >
              <ArrowUpTrayIcon className="w-4 h-4" />
              {isSaving ? 'Uploading...' : `Upload ${selectedImages.length} to Google Ads`}
            </Button>
          )}
        </div>

      </div>

      {/* Image Picker Modal — TargetedImagePicker shows the crop modal for
          the placement bound to `selectedImageType`. The previous in-picker
          type switcher (`imageTypeOptions` on plain `ImagePicker`) is moved
          to the parent-side selector adjacent to "Select from Media Library"
          so each picker session is scoped to one placement (the cropper
          needs a single aspect/spec). */}
      <TargetedImagePicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        usageContext={{
          platform: 'google_ads',
          placementKey: `google_ads.${selectedImageType}`,
        }}
        onConfirm={handlePendingSelected}
        maxSelection={20}
      />

      {/* Remove Image Confirmation Dialog */}
      <ConfirmDialog
        isOpen={imageToRemove !== null}
        onClose={() => setImageToRemove(null)}
        onConfirm={handleRemoveLinkedImage}
        title={`Remove Image from ${entityTypeLabel}`}
        message={`Are you sure you want to remove "${imageToRemove?.filename || 'this image'}" from the ${entityTypeLabel.toLowerCase()}? The image will remain in your Media Library but will no longer be linked to Google Ads.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={isRemoving}
      />

      {/* Google Ads Account Restriction Dialog */}
      {restrictionDetails && (
        <GoogleRestrictionDialog
          isOpen={true}
          onClose={() => setRestrictionDetails(null)}
          details={restrictionDetails}
        />
      )}

      {/* Change Image Type Dialog with Crop Preview */}
      {imageToChangeType && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setImageToChangeType(null);
              setNewImageType(null);
            }}
          />

          {/* Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Change Image Type
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              The original image from the Media Library will be re-cropped and uploaded to Google Ads.
            </p>

            {/* Type selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New type:
              </label>
              <select
                value={newImageType || ''}
                onChange={(e) => setNewImageType(e.target.value as ImageAssetType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a type...</option>
                {imageTypeOptions.filter(
                  (opt: ImageTypeOption) => opt.value !== imageToChangeType.image_field_type
                ).map((option: ImageTypeOption) => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.aspectRatio})
                  </option>
                ))}
              </select>
            </div>

            {/* Side-by-side crop preview */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Current crop */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 text-center">
                  Current: {imageTypeOptions.find((opt) => opt.value === imageToChangeType.image_field_type)?.aspectRatio || 'Unknown'}
                </p>
                <div
                  className="bg-gray-100 rounded-lg overflow-hidden mx-auto border border-gray-200"
                  style={{
                    width: '100%',
                    maxWidth: 200,
                    aspectRatio: (() => {
                      const opt = imageTypeOptions.find((o) => o.value === imageToChangeType.image_field_type);
                      if (!opt) return 1;
                      const [w, h] = opt.aspectRatio.split(':').map(Number);
                      return w / h;
                    })(),
                  }}
                >
                  {imageToChangeType.thumbnail_url && (
                    <img
                      src={imageToChangeType.thumbnail_url}
                      alt={imageToChangeType.filename}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              </div>

              {/* New crop preview */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 text-center">
                  {newImageType
                    ? `New: ${imageTypeOptions.find((opt) => opt.value === newImageType)?.aspectRatio}`
                    : 'Select a new type'}
                </p>
                <div
                  className="bg-gray-100 rounded-lg overflow-hidden mx-auto border-2 transition-all duration-200"
                  style={{
                    width: '100%',
                    maxWidth: 200,
                    aspectRatio: (() => {
                      if (!newImageType) return 1;
                      const opt = imageTypeOptions.find((o) => o.value === newImageType);
                      if (!opt) return 1;
                      const [w, h] = opt.aspectRatio.split(':').map(Number);
                      return w / h;
                    })(),
                    borderColor: newImageType ? '#3b82f6' : '#e5e7eb',
                  }}
                >
                  {imageToChangeType.thumbnail_url && newImageType ? (
                    <img
                      src={imageToChangeType.thumbnail_url}
                      alt={imageToChangeType.filename}
                      className="w-full h-full object-cover transition-all duration-200"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <PhotoIcon className="w-8 h-8" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setImageToChangeType(null);
                  setNewImageType(null);
                }}
                disabled={isChangingType}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleChangeImageType}
                disabled={!newImageType || isChangingType}
              >
                {isChangingType ? 'Re-cropping & Uploading...' : 'Change Type'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
